import * as _ from "underscore";

import { IWorksheetState } from "./interfaces";
import { WS_NAME } from "./wsDefs";
import { assertVal, errWithInfo } from "./debug";
import { getWsById, getWsByName, hasWsId } from "./wsHelpers";

import {
  TYPE_KEY,
  WS_GET_LOCK,
  WS_CREATE,
  WS_IMPORT,
  WS_FETCH,
  WS_UPDATE,
  WS_DELETE_FROM_FIELD,
} from "./types";

const uuid = require("uuid");

/* helpers */

const worksheetsForApp = (state, appId): IWorksheetState[] =>
  _.where(state.worksheetEditor.worksheets, { appId });

/* higher-order actions */

export const initLoanFileWs = (appId, wsId) => (dispatch, getState) => {
  assertVal(appId, `${TYPE_KEY}initLoanFileWs`, "appId", appId);

  const worksheets = worksheetsForApp(getState(), appId);

  if (getWsByName(worksheets, WS_NAME.LOAN_FILE)) {
    return Promise.resolve();
  }
  if (wsId) {
    return wsFetch(appId, WS_NAME.LOAN_FILE, wsId)(dispatch);
  }
  return wsCreate(appId, WS_NAME.LOAN_FILE)(dispatch);
};

export const initWsByName = (appId, wsName) => (dispatch, getState) => {
  assertVal(appId, `${TYPE_KEY}initWsByName`, "appId", appId);
  assertVal(appId, `${TYPE_KEY}initWsByName`, "wsName", wsName);

  const worksheets = worksheetsForApp(getState(), appId);

  const loanFileWs = getWsByName(worksheets, WS_NAME.LOAN_FILE);
  if (!loanFileWs) {
    throw new Error(`must init ${WS_NAME.LOAN_FILE} before named worksheet`);
  }
  if (!loanFileWs.instance) {
    return Promise.reject(`${WS_NAME.LOAN_FILE} not ready`);
  }
  if (wsName === WS_NAME.LOAN_FILE) {
    return Promise.resolve(); // all done if loading loan file itself
  }
  if (!loanFileWs.instance.hasOwnProperty(wsName)) {
    throw new Error(`cannot find ${wsName} on ${WS_NAME.LOAN_FILE}`);
  }

  const namedWsId = loanFileWs.instance[wsName];
  if (hasWsId(worksheets, namedWsId)) {
    return Promise.resolve(); // already loaded
  }

  return wsGetLock(appId, loanFileWs.id)(dispatch, getState)
    .then(() => wsCreate(appId, wsName)(dispatch))
    .then(
      ({ worksheet }) =>
        wsUpdate(
          appId,
          loanFileWs.name,
          loanFileWs.id,
          loanFileWs.instance.version,
          wsName,
          worksheet.id,
        )(dispatch), // lock released when update succeeds/fails
    );
};

export const initWsById = (appId, wsName, wsId) => (dispatch, getState) => {
  assertVal(appId, `${TYPE_KEY}initWsById`, "appId", appId);
  assertVal(appId, `${TYPE_KEY}initWsById`, "wsName", wsName);
  assertVal(appId, `${TYPE_KEY}initWsById`, "wsId", wsId);

  const worksheets = worksheetsForApp(getState(), appId);

  const loanFileWs = getWsByName(worksheets, WS_NAME.LOAN_FILE);
  if (!loanFileWs) {
    throw new Error(`must init ${WS_NAME.LOAN_FILE} before worksheet`);
  }
  if (!loanFileWs.instance) {
    return Promise.reject(`${WS_NAME.LOAN_FILE} not ready`);
  }

  // no path to create, since it must exist already if looking by id
  if (!hasWsId(worksheets, wsId)) {
    throw new Error(`cannot find worksheet ${wsName} with id ${wsId}`);
  }

  return Promise.resolve();
};

/* base actions */

const wsGetLock = (appId, wsId) => (dispatch, getState) => {
  assertVal(appId, WS_GET_LOCK, "appId", appId);
  assertVal(appId, WS_GET_LOCK, "wsId", wsId);

  const worksheets = worksheetsForApp(getState(), appId);

  const ws = getWsById(worksheets, wsId);
  if (!ws) {
    errWithInfo(appId, WS_GET_LOCK, `cannot find ${wsId}`);
  }
  if (ws.locked === null) {
    errWithInfo(appId, WS_GET_LOCK, `${wsId} not yet initialized`);
  }
  if (ws.locked) {
    return Promise.reject(`${wsId} already locked`);
  }

  dispatch({
    type: WS_GET_LOCK,
    data: {
      appId,
      wsId,
    },
  });

  return Promise.resolve();
};

export const wsCreate = (appId, wsName) => dispatch => {
  assertVal(appId, WS_CREATE, "appId", appId);
  assertVal(appId, WS_CREATE, "wsName", wsName);

  return dispatch({
    type: WS_CREATE,
    ajax: {
      url: `/worksheets`,
      method: "POST",
      params: {
        application_id: appId,
        worksheet_name: wsName,
      },
      meta: {
        appId,
        wsName,
        tempId: uuid.v4(),
      },
    },
  });
};

export const wsImport = (appId, wsName, wsId) => dispatch => {
  assertVal(appId, WS_IMPORT, "appId", appId);
  assertVal(appId, WS_IMPORT, "wsName", wsName);
  assertVal(appId, WS_IMPORT, "wsId", wsId);

  return dispatch({
    type: WS_IMPORT,
    ajax: {
      url: `/uw/${appId}/worksheets/populate`,
      method: "POST",
      params: {
        worksheet_id: wsId,
      },
      meta: {
        appId,
        wsName,
        wsId,
      },
    },
  });
};

export const wsFetch = (appId, wsName, wsId) => dispatch => {
  assertVal(appId, WS_FETCH, "appId", appId);
  assertVal(appId, WS_FETCH, "wsName", wsName);
  assertVal(appId, WS_FETCH, "wsId", wsId);

  return dispatch({
    type: WS_FETCH,
    ajax: {
      url: `/worksheets/${wsId}`,
      method: "GET",
      meta: {
        appId,
        wsName,
        wsId,
      },
    },
  });
};

export const wsUpdate = (appId, wsName, wsId, version, field, value) => dispatch => {
  assertVal(appId, WS_UPDATE, "appId", appId);
  assertVal(appId, WS_UPDATE, "wsId", wsId);
  assertVal(appId, WS_UPDATE, "wsName", wsName);
  assertVal(appId, WS_UPDATE, "version", version);
  assertVal(appId, WS_UPDATE, "field", field);

  return dispatch({
    type: WS_UPDATE,
    ajax: {
      url: `/worksheets/${wsId}`,
      method: "POST",
      params: {
        application_id: appId,
        name: wsName,
        version,
        updates: [
          {
            name: field,
            value,
          },
        ],
      },
      meta: {
        appId,
        wsName,
        wsId,
        edit: {
          field,
          value,
        },
      },
    },
  });
};

export const wsDeleteFromField = (appId, wsId, version, field, index) => dispatch => {
  assertVal(appId, WS_DELETE_FROM_FIELD, "appId", appId);
  assertVal(appId, WS_DELETE_FROM_FIELD, "wsId", wsId);
  assertVal(appId, WS_DELETE_FROM_FIELD, "version", version);
  assertVal(appId, WS_DELETE_FROM_FIELD, "field", field);

  return dispatch({
    type: WS_DELETE_FROM_FIELD,
    ajax: {
      url: `/worksheets/${wsId}`,
      method: "POST",
      params: {
        version,
        application_id: appId,
        updates: [
          {
            name: field,
            action: "delete",
            index,
          },
        ],
      },
      meta: {
        appId,
        wsId,
        edit: {
          field,
          index,
        },
      },
    },
  });
};
